import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Edit, Save, Trash2, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { operatorService } from '../../services/operator.service';
import type { Operator, OperatorInput, OperatorStatus, OperatorType } from '../../types/crm';
import Loading from '../../components/Loading';
import ContactList from '../../components/crm/ContactList';
import ActivityList from '../../components/crm/ActivityList';
import OperatorOpportunityList from '../../components/crm/OperatorOpportunityList';

const OPERATOR_TYPES: OperatorType[] = ['restaurant', 'hotel', 'healthcare', 'education', 'catering', 'other'];
const OPERATOR_STATUSES: OperatorStatus[] = ['prospect', 'active', 'inactive', 'lost'];

const editOperatorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  operator_type: z.enum(['restaurant', 'hotel', 'healthcare', 'education', 'catering', 'other']),
  status: z.enum(['prospect', 'active', 'inactive', 'lost']),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  website: z.string().optional(),
  notes: z.string().optional(),
});

type EditOperatorFormData = z.infer<typeof editOperatorSchema>;

const titleize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

type TabKey = 'contacts' | 'opportunities' | 'activities';

export const OperatorDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const operatorId = id ? parseInt(id, 10) : NaN;

  const [operator, setOperator] = useState<Operator | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('contacts');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditOperatorFormData>({
    resolver: zodResolver(editOperatorSchema),
  });

  useEffect(() => {
    if (Number.isNaN(operatorId)) return;
    loadOperator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId]);

  const loadOperator = async () => {
    setIsLoading(true);
    try {
      const op = await operatorService.getById(operatorId);
      setOperator(op);
      reset({
        name: op.name,
        operator_type: op.operator_type,
        status: op.status,
        email: op.email ?? '',
        phone: op.phone ?? '',
        address: op.address ?? '',
        city: op.city ?? '',
        state: op.state ?? '',
        zip: op.zip ?? '',
        website: op.website ?? '',
        notes: op.notes ?? '',
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async (data: EditOperatorFormData) => {
    setIsSaving(true);
    try {
      const input: Partial<OperatorInput> = {
        name: data.name,
        operator_type: data.operator_type,
        status: data.status,
        email: data.email || null,
        phone: data.phone || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        zip: data.zip || null,
        website: data.website || null,
        notes: data.notes || null,
      };
      const updated = await operatorService.update(operatorId, input);
      setOperator(updated);
      setIsEditing(false);
      toast.success('Operator updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirm('Delete this operator? This will also delete its opportunities.')) return;
    try {
      await operatorService.delete(operatorId);
      toast.success('Operator deleted');
      navigate('/crm/operators');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) return <Loading />;
  if (!operator) return <div className="text-gray-500">Operator not found.</div>;

  return (
    <div className="space-y-6">
      <Link to="/crm/operators" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to operators
      </Link>

      <div className="card p-6">
        {isEditing ? (
          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            <div>
              <label htmlFor="name" className="label">Name</label>
              <input {...register('name')} id="name" type="text" className="input" />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="operator_type" className="label">Type</label>
                <select {...register('operator_type')} id="operator_type" className="input">
                  {OPERATOR_TYPES.map((t) => (
                    <option key={t} value={t}>{titleize(t)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="status" className="label">Status</label>
                <select {...register('status')} id="status" className="input">
                  {OPERATOR_STATUSES.map((s) => (
                    <option key={s} value={s}>{titleize(s)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="label">Email</label>
                <input {...register('email')} id="email" type="email" className="input" />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              </div>
              <div>
                <label htmlFor="phone" className="label">Phone</label>
                <input {...register('phone')} id="phone" type="text" className="input" />
              </div>
            </div>

            <div>
              <label htmlFor="address" className="label">Address</label>
              <input {...register('address')} id="address" type="text" className="input" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="city" className="label">City</label>
                <input {...register('city')} id="city" type="text" className="input" />
              </div>
              <div>
                <label htmlFor="state" className="label">State</label>
                <input {...register('state')} id="state" type="text" className="input" />
              </div>
              <div>
                <label htmlFor="zip" className="label">Zip</label>
                <input {...register('zip')} id="zip" type="text" className="input" />
              </div>
            </div>

            <div>
              <label htmlFor="website" className="label">Website</label>
              <input {...register('website')} id="website" type="text" className="input" />
            </div>

            <div>
              <label htmlFor="notes" className="label">Notes</label>
              <textarea {...register('notes')} id="notes" rows={3} className="input" />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => { setIsEditing(false); loadOperator(); }} className="btn btn-secondary">
                <X className="h-4 w-4 inline mr-1" />
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className="btn btn-primary">
                <Save className="h-4 w-4 inline mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{operator.name}</h1>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{titleize(operator.operator_type)}</span>
                  <span className="text-gray-300">·</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                    operator.status === 'active' ? 'bg-green-100 text-green-800' :
                    operator.status === 'prospect' ? 'bg-yellow-100 text-yellow-800' :
                    operator.status === 'lost' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {titleize(operator.status)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setIsEditing(true)} className="btn btn-secondary">
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button onClick={onDelete} className="btn btn-secondary text-(--color-text-danger)">
                  <Trash2 className="h-4 w-4 inline mr-1" />
                  Delete
                </button>
              </div>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="text-gray-900">{operator.email || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Phone</dt>
                <dd className="text-gray-900">{operator.phone || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Address</dt>
                <dd className="text-gray-900">
                  {[operator.address, [operator.city, operator.state].filter(Boolean).join(', '), operator.zip]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Website</dt>
                <dd className="text-gray-900">{operator.website || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Distributor</dt>
                <dd className="text-gray-900">{operator.distributor?.name || '—'}</dd>
              </div>
              {operator.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notes</dt>
                  <dd className="text-gray-900 whitespace-pre-wrap">{operator.notes}</dd>
                </div>
              )}
            </dl>
          </>
        )}
      </div>

      <div>
        <nav className="flex gap-4 border-b border-gray-200" aria-label="Operator tabs">
          {(['contacts', 'opportunities', 'activities'] as TabKey[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {titleize(tab)}
            </button>
          ))}
        </nav>

        <div className="mt-6">
          {activeTab === 'contacts' && <ContactList operatorId={operator.id} />}
          {activeTab === 'opportunities' && <OperatorOpportunityList operatorId={operator.id} />}
          {activeTab === 'activities' && <ActivityList operatorId={operator.id} />}
        </div>
      </div>
    </div>
  );
};

export default OperatorDetail;
